import React, { useState } from 'react';
import { Search, Camera, ChevronDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SearchField {
  id: string;
  label: string;
  placeholder: string;
}

interface StickySearchBarProps {
  onSearch: (query: string, field: string) => void;
  onSearchFieldChange: (field: string) => void;
  showShadow?: boolean;
  enableScan?: boolean;
  searchFields?: SearchField[]; // Add searchFields as a prop
}

const defaultSearchFields: SearchField[] = [
  {
    id: 'all',
    label: 'ทั้งหมด',
    placeholder: 'พิมพ์รหัสแม่พิมพ์/Part No.',
  },
  { id: 'moldId', label: 'รหัสแม่พิมพ์', placeholder: 'พิมพ์รหัสแม่พิมพ์' },
  { id: 'partNo', label: 'Part No.', placeholder: 'พิมพ์ Part No.' },
  { id: 'partName', label: 'ชื่อชิ้นงาน', placeholder: 'พิมพ์ชื่อชิ้นงาน' },
  { id: 'customer', label: 'ลูกค้า', placeholder: 'พิมพ์ชื่อลูกค้า' },
];

const StickySearchBar: React.FC<StickySearchBarProps> = ({
  onSearch,
  onSearchFieldChange,
  showShadow,
  enableScan = false,
  searchFields = defaultSearchFields, // Use defaultSearchFields if no searchFields prop is provided
}) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<SearchField>(
    searchFields[0],
  );
  const [searchQuery, setSearchQuery] = useState('');

  const handleFieldSelect = (field: SearchField) => {
    setSelectedField(field);
    setIsDropdownOpen(false);
    onSearchFieldChange(field.id);
    onSearch(searchQuery, field.id);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query, selectedField.id);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch('', selectedField.id);
  };

  return (
    <div
      className={`sticky top-0 z-40 bg-white transition-all duration-300 ${showShadow ? 'shadow-md' : ''} py-4`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative flex-grow flex flex-row justify-between items-center gap-2">
          <div className="relative flex-grow">
            <div className="flex">
              {/* Search Field Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="h-full px-4 py-2 bg-gray-50 border border-r-0 rounded-l-full flex items-center gap-2 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700 whitespace-nowrap">
                    {selectedField.label}
                  </span>
                  <ChevronDown size={16} className="text-gray-500" />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {searchFields.map((field) => (
                      <button
                        key={field.id}
                        onClick={() => handleFieldSelect(field)}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                          selectedField.id === field.id
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700'
                        }`}
                      >
                        {field.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Input */}
              <div className="relative flex-grow">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
                <input
                  type="text"
                  value={searchQuery}
                  placeholder={selectedField.placeholder}
                  className="w-full pl-10 pr-10 py-2 border border-l-0 rounded-r-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onChange={(e) => handleSearch(e.target.value)}
                  data-cy="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Camera Button */}
          { enableScan && <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" onClick={() => navigate('/scan')}>
            <Camera className="text-gray-500" size={24} />
          </button> }
        </div>

        {/* Active Filters */}
        {selectedField.id !== 'all' && searchQuery && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-500">กำลังค้นหา:</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
              {selectedField.label}
              <button
                onClick={() => handleFieldSelect(searchFields[0])}
                className="hover:text-blue-800"
              >
                <X size={14} />
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickySearchBar;
